
import React from 'react';
import { TrendingUp, TrendingDown, Target } from 'lucide-react';

interface PredictionScenariosProps {
  metal: 'ORO' | 'ARGENTO';
  scenarios: {
    high: { price: number; percentage: number; probability: number };
    average: { price: number; percentage: number; probability: number };
    low: { price: number; percentage: number; probability: number };
  };
}

export function PredictionScenarios({ metal, scenarios }: PredictionScenariosProps) {
  const metalTheme = metal === 'ORO' ? 'gold-theme' : 'silver-theme';

  const ScenarioCard = ({ 
    title, 
    price, 
    percentage, 
    probability, 
    variant 
  }: {
    title: string;
    price: number;
    percentage: number;
    probability: number;
    variant: 'high' | 'average' | 'low';
  }) => {
    const getIcon = () => {
      if (variant === 'high') return <TrendingUp size={20} className="text-green-600" />;
      if (variant === 'low') return <TrendingDown size={20} className="text-red-600" />;
      return <Target size={20} className="text-metal" />;
    };

    const getCardClasses = () => {
      const baseClasses = "metal-card p-5 hover:shadow-xl transition-all duration-300 hover:scale-[1.01]";
      
      if (variant === 'high') return `${baseClasses} border-l-4 border-l-green-500`;
      if (variant === 'low') return `${baseClasses} border-l-4 border-l-red-500`;
      return `${baseClasses} border-l-4 border-l-metal`;
    };

    const getPercentageColor = () => {
      if (percentage > 0) return 'text-green-600';
      if (percentage < 0) return 'text-red-600';
      return 'text-metal';
    };

    const getProbabilityBarColor = () => {
      if (variant === 'high') return 'bg-green-500';
      if (variant === 'low') return 'bg-red-500';
      return 'bg-metal';
    };

    const probabilityWidth = `${probability}%`;

    return (
      <div className={getCardClasses()}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            {getIcon()}
            <h4 className="font-bold text-lg text-foreground">{title}</h4>
          </div>
          <div className="text-xs text-muted-foreground bg-muted/50 px-2 py-1 rounded-full">
            {probability}% prob.
          </div>
        </div>
        
        <div className="space-y-3">
          <div className="text-center">
            <div className="text-2xl font-black text-foreground">
              €{price.toFixed(2)}
            </div>
            <div className="text-xs text-muted-foreground">per grammo</div>
          </div>
          
          <div className="text-center">
            <div className={`text-lg font-bold ${getPercentageColor()}`}>
              {percentage > 0 ? '+' : ''}{percentage.toFixed(1)}%
            </div>
            <div className="text-xs text-muted-foreground">variazione</div>
          </div>
          
          <div className="mt-4">
            <div className="text-xs text-muted-foreground mb-1">Probabilità</div>
            <div className="w-full bg-muted rounded-full h-2">
              <div 
                className={`h-2 rounded-full transition-all duration-500 ${getProbabilityBarColor()}`}
                style={{ width: probabilityWidth }}
              ></div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`${metalTheme}`}>
      <div className="metal-card p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 rounded-lg metal-surface">
            <Target className="h-5 w-5 text-metal" />
          </div>
          <h3 className="text-xl font-bold text-metal">
            Scenari di Previsione
          </h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <ScenarioCard
            title="Scenario Ottimistico"
            price={scenarios.high.price}
            percentage={scenarios.high.percentage}
            probability={scenarios.high.probability}
            variant="high"
          />
          
          <ScenarioCard
            title="Scenario Probabile"
            price={scenarios.average.price}
            percentage={scenarios.average.percentage}
            probability={scenarios.average.probability}
            variant="average"
          />
          
          <ScenarioCard
            title="Scenario Conservativo"
            price={scenarios.low.price}
            percentage={scenarios.low.percentage}
            probability={scenarios.low.probability}
            variant="low"
          />
        </div>
      </div>
    </div>
  );
}
